import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { get } from 'lodash-es';
import Card from 'reactstrap/lib/Card';
import { EventToolbox } from 'client/utils/event-toolbox';
import { TrackingConstant } from 'client/tracking/constant';
import { BuildPriceEntities } from 'client/data/models/build-price';
import { VehicleEntities } from 'client/data/models/vehicle-v2';
import { RandomPropTypes } from 'client/data/models/random';
import { CHART_PROPS_REDESIGN } from 'site-modules/shared/constants/price-checker';
import { BARS_PER_DEAL } from 'site-modules/shared/constants/build-price';
import { getPriceString } from 'site-modules/shared/utils/price-utils';
import {
  getDataPointsFromTransactionCount,
  getEspBasedTmvChartData,
  getTransactionsCountOverlayConfig,
} from 'site-modules/shared/utils/pricing-chart';
import { Gradient } from 'site-modules/shared/components/price-checker/price-checker-promo-chart/overlays/gradient/gradient';
import { PopoverItem } from 'site-modules/shared/components/popover-item/popover-item';
import { PricingChart } from 'site-modules/shared/components/price-checker/price-checker-promo-chart/pricing-chart';
import { getPlural } from 'client/utils/plural';

const OVERLAY_TRACKING_VALUES = ['great_price', 'good_price', 'fair_price', 'high_price'];
const DESCRIPTION_ID = 'summary-chart-description';

export function trackChartClick({ currentTarget }) {
  EventToolbox.fireTrackAction({
    event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
    event_data: {
      action_name: TrackingConstant.ACTION_TOOL_SELECT,
      subaction_name: TrackingConstant.SUBACTION_CHART_CLICK,
      creative_id: 'bp_price_chart',
      action_category: TrackingConstant.USER_ACTION_CATEGORY,
      action_cause: 'click',
      value: currentTarget.dataset.trackingValue,
    },
  });
}

export function PriceAnalysisChart({
  className = null,
  modelYear: { year, makeName, modelName },
  priceSummary,
  transactionCount = null,
  transactionsCountAdjustments = null,
  isDealerless = false,
  isMobile = false,
}) {
  const tmvChartData = getEspBasedTmvChartData(priceSummary.esp);

  let barsPerDeal = BARS_PER_DEAL.DEFAULT;
  let overlayPricingChartProps = { overlayConfig: null, yStartingPoint: 64, yRate: 2 };
  let transactionCountChartProps = {
    dataPoints: CHART_PROPS_REDESIGN.DATA_POINTS.WITH_DATA,
    columnWidth: { desktop: 15, mobile: 15 },
  };

  let currentTransactionCount;
  if (transactionCount && tmvChartData) {
    currentTransactionCount = Object.values(get(transactionCount, 'transactionCount', {})).some(value => value)
      ? get(transactionCount, 'transactionCount')
      : get(transactionCount, 'nationalTransactionCount');

    barsPerDeal = BARS_PER_DEAL.WITH_COUNTS;
    transactionCountChartProps = {
      columnWidth: { desktop: 26, mobile: 26 },
      dataPoints: getDataPointsFromTransactionCount({
        transactionCount: currentTransactionCount,
        transactionsCountAdjustments,
        barsPerDeal,
        minValue: 0,
        maxValue: 20,
      }),
      isStepGradient: true,
    };

    const overlayConfig = getTransactionsCountOverlayConfig({
      transactionCount: currentTransactionCount,
      component: Gradient,
      barsPerDeal,
      alwaysShow: true,
    });

    overlayPricingChartProps = {
      yStartingPoint: 95,
      yRate: 2.5,
      overlayConfig: {
        ...overlayConfig,
        overlaysProps: overlayConfig.overlaysProps.map((overlayProps, index) => ({
          ...overlayProps,
          elementProps: {
            component: 'a',
            href: `#chart-overlay-${index}`,
            id: `chart-overlay-${index}`,
            'aria-label': `${overlayProps.text} ${overlayProps.textLabel}`,
            role: 'img',
            tabIndex: 0,
            onMouseEnter: trackChartClick,
            'data-tracking-value': OVERLAY_TRACKING_VALUES[index],
          },
        })),
      },
    };
  }

  return (
    <Card className={classnames('price-analysis-chart py-1_5 px-1 px-md-2 align-items-center', className)}>
      <div className={classnames({ 'mb-1_5': isDealerless })}>
        <dl className="medium">
          {priceSummary.items.map(({ label, value, tooltipText }) => (
            <div key={`chart-${label}`} className="mb-0_25 d-flex justify-content-between">
              <dt className="fw-normal pe-0_5">
                {label}
                {!!tooltipText && (
                  <PopoverItem iconClassName="medium text-primary-darker" screenReaderTooltipLabel={label}>
                    {tooltipText}
                  </PopoverItem>
                )}
              </dt>
              <dd className="mb-0 fw-bold">{value}</dd>
            </div>
          ))}
        </dl>
        <Card className="esp-card size-30 fw-bold align-items-center pt-0_75 pb-0_5 px-1">
          <div className="heading-5 mb-0_25">{priceSummary.espLabel}</div>
          {getPriceString(priceSummary.esp)}
        </Card>
      </div>
      {isDealerless && (
        <div className="text-center">
          The {year} {makeName} {modelName} has a price set by the manufacturer that is not negotiable. In this case,{' '}
          <span className="fw-bold">Edmunds suggests you pay MSRP</span> minus any incentives & rebates.
        </div>
      )}
      {!isDealerless && (
        <Fragment>
          <PricingChart
            {...CHART_PROPS_REDESIGN.COMMON}
            {...overlayPricingChartProps}
            {...transactionCountChartProps}
            svgRole="group"
            strokeLength={{ desktop: 6, mobile: 6 }}
            arrowSize={16}
            customPriceLabels={[
              {
                label: getPriceString(tmvChartData.maxGreatPrice),
                index: barsPerDeal,
              },
              {
                label: getPriceString(tmvChartData.maxFairPrice),
                index: barsPerDeal * 3 - 1,
                end: true,
              },
            ]}
            selectedPriceIndex={barsPerDeal * 2 - 0.5}
            isMobile={isMobile}
            className="d-flex justify-content-center w-100"
            customLabelClassName="small text-lowercase"
            customPriceLabelClassName="small fw-bold"
            ariaDescribedBy={DESCRIPTION_ID}
          />
          <span id={DESCRIPTION_ID} className="visually-hidden">
            Other people are paying {getPriceString(tmvChartData.maxGoodPrice)} for similarly equipped vehicles. Below
            {getPriceString(tmvChartData.maxGreatPrice)} is a great price. {getPriceString(tmvChartData.maxGreatPrice)}{' '}
            to {getPriceString(tmvChartData.maxGoodPrice)} is a good price. {getPriceString(tmvChartData.maxGoodPrice)}{' '}
            to {getPriceString(tmvChartData.maxFairPrice)} is a fair price. Above{' '}
            {getPriceString(tmvChartData.maxFairPrice)} is a high price.{' '}
            {!!currentTransactionCount && (
              <Fragment>
                There have been {get(currentTransactionCount, 'tier1', 0)}{' '}
                {getPlural('sale', get(currentTransactionCount, 'tier1', 0))} in the great price range,{' '}
                {get(currentTransactionCount, 'tier2', 0)} {getPlural('sale', get(currentTransactionCount, 'tier2', 0))}{' '}
                in the good price range, {get(currentTransactionCount, 'tier3', 0)}{' '}
                {getPlural('sale', get(currentTransactionCount, 'tier3', 0))} in the fair price range, and{' '}
                {get(currentTransactionCount, 'tier4', 0)} {getPlural('sale', get(currentTransactionCount, 'tier4', 0))}{' '}
                in the high price range.
              </Fragment>
            )}
          </span>
          {priceSummary.hasDiscount ? (
            <div className="small">
              The <span className="fw-bold">Edmunds Suggested Price</span> is what we recommend you pay, including
              destination fee but excluding taxes and other fees. It is based on our analysis of millions of data
              points, including supply, demand, incentives, options, and recent nearby transactions.
            </div>
          ) : (
            <div className="small">
              The price shown above is based on our analysis of millions of data points, including supply, demand,
              incentives, options, and recent nearby transactions. We determined{' '}
              <span className="fw-bold">what others are paying</span> for similarly equipped vehicles, including
              destination fee but excluding taxes and other fees.
            </div>
          )}
        </Fragment>
      )}
    </Card>
  );
}

PriceAnalysisChart.propTypes = {
  modelYear: BuildPriceEntities.ModelYear.isRequired,
  priceSummary: PropTypes.shape({
    esp: PropTypes.number,
    espLabel: PropTypes.string,
    hasDiscount: PropTypes.bool,
    items: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.string,
        tooltipText: PropTypes.string,
      })
    ),
  }).isRequired,
  transactionCount: VehicleEntities.StyleTransactionCount,
  transactionsCountAdjustments: RandomPropTypes.TransactionsCountAdjustments,
  className: PropTypes.string,
  isDealerless: PropTypes.bool,
  isMobile: PropTypes.bool,
};
